import { useEffect, useState } from 'react';
import { CircleUser, Home, Loader2, LogOut, MapPin } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { getActiveMap } from '@/map/context';
import { EventsCenter } from '@/components/layout/EventsCenter';
import { NotificationsCenter } from '@/components/layout/NotificationsCenter';
import { useAuth } from '@/store/auth';
import { useConnection } from '@/store/connection';
import { useOperatorPos } from '@/store/operatorPos';
import { useSettings } from '@/store/settings';
import { useFleet } from '@/store/fleet';
import { currentAppVersion } from '@/lib/updater';
import { cn } from '@/lib/utils';

/** UTC clock string formatted "JUN 10 2026, 14:08:02Z". */
function useUtcClock(): string {
  const [now, setNow] = useState(() => fmtUtc(new Date()));
  useEffect(() => {
    const id = window.setInterval(() => setNow(fmtUtc(new Date())), 1000);
    return () => window.clearInterval(id);
  }, []);
  return now;
}

function fmtUtc(d: Date): string {
  const mon = d.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' }).toUpperCase();
  const day = d.getUTCDate();
  const yr = d.getUTCFullYear();
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  const ss = String(d.getUTCSeconds()).padStart(2, '0');
  return `${mon} ${day} ${yr}, ${hh}:${mm}:${ss}Z`;
}

export function TopBar() {
  const status = useConnection((s) => s.status);
  const armedCount = useFleet(
    (s) => Object.values(s.states).filter((st) => st.arm === 'armed').length,
  );
  const utc = useUtcClock();
  const online = status === 'connected';

  return (
    <header
      className="flex flex-none items-center gap-3 border-b border-border-default bg-surface-1 px-3 sm:gap-6 sm:px-4"
      // height includes safe-area-inset-top; padding pushes content below
      // it so the camera notch / status bar sits in empty branded space.
      style={{
        height: 'var(--rail-topbar)',
        paddingTop: 'env(safe-area-inset-top, 0px)',
        boxSizing: 'border-box',
      }}
    >
      {/* Logo + wordmark — wordmark text scales / hides at narrow widths. */}
      <Link
        to="/"
        className="flex min-w-0 items-center gap-2"
        title={`Console v${currentAppVersion()} — powered by fabricOS`}
      >
        <img
          src="/brand/logo-mark-light.svg"
          alt=""
          className="h-[15px] w-[17px] flex-none opacity-90"
        />
        <span className="truncate text-[13px] font-semibold tracking-tight text-fg-primary sm:text-[14px]">
          Console
        </span>
        <span className="mt-0.5 hidden text-[10px] text-fg-muted md:inline">
          powered by fabricOS
        </span>
        <span className="mt-0.5 font-mono text-[9px] tracking-tight text-fg-muted/70 tabular-nums">
          v{currentAppVersion()}
        </span>
      </Link>

      {/* LIVE pill — visible only when something is armed. */}
      {armedCount > 0 && (
        <span
          className="inline-flex flex-none items-center gap-1.5 rounded-sm px-2 py-0.5"
          style={{ background: 'var(--color-status-critical-wash)' }}
          title={`${armedCount} airborne`}
        >
          <span className="h-1.5 w-1.5 rounded-full bg-status-critical animate-pulse" />
          <span className="text-[10px] font-semibold tracking-[0.06em] text-status-critical">
            LIVE
          </span>
        </span>
      )}

      {/* Centered cluster: Online + UTC clock. Clock is hidden under sm to
          buy back horizontal room; status pill stays visible always. */}
      <div className="ml-auto mr-auto flex min-w-0 items-center gap-2 text-[12px]">
        <span
          className={cn(
            'h-1.5 w-1.5 flex-none rounded-full',
            online && 'bg-status-good shadow-[0_0_6px_currentColor]',
            status === 'connecting' && 'bg-status-warn animate-pulse',
            status === 'reconnecting' && 'bg-status-warn animate-pulse',
            status === 'disconnected' && 'bg-status-critical',
          )}
        />
        <span
          className={cn(
            'truncate font-medium',
            online ? 'text-status-good' : 'text-fg-secondary',
          )}
        >
          {online ? 'Online' : status}
        </span>
        <span className="hidden font-mono text-fg-secondary tabular-nums sm:inline">
          {utc}
        </span>
        <HomeChip />
        <BrowserLocation />
      </div>

      {/* Right cluster: notifications · events · user. */}
      <div className="flex flex-none items-center gap-1">
        <NotificationsCenter />
        <EventsCenter />
        <UserMenu />
      </div>
    </header>
  );
}

/** Home-position chip: the settings default view / future HQ. Double-click
 *  flies the map there (matches the pilot chip's gesture). */
function HomeChip() {
  const lat = useSettings((s) => s.map.initialLat);
  const lon = useSettings((s) => s.map.initialLon);
  return (
    <button
      type="button"
      onDoubleClick={() =>
        getActiveMap()?.easeTo({ center: [lon, lat], zoom: 15, duration: 600 })
      }
      title="Home position (Settings → Map) — double-click to fly there"
      className="hidden items-center gap-1 font-mono text-[11px] tabular-nums text-fg-secondary hover:text-amber-300 md:flex"
    >
      <Home className="h-3.5 w-3.5 text-amber-400/80" />
      {lat.toFixed(5)}, {lon.toFixed(5)}
    </button>
  );
}

/**
 * Operator geolocation chip. Click the pin to ask the browser for the
 * current position; the coords render beside it and double-clicking them
 * flies the active map there. One-shot — click the pin again to refresh.
 */
function BrowserLocation() {
  const [state, setState] = useState<
    { kind: 'idle' } | { kind: 'loading' } | { kind: 'ok'; lat: number; lon: number } | { kind: 'error' }
  >({ kind: 'idle' });

  // "Home follows pilot" needs a position from boot, not only after a
  // manual pin click.
  useEffect(() => {
    if (useSettings.getState().map.homeFollowsPilot) fetchLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function fetchLocation() {
    if (!('geolocation' in navigator)) {
      setState({ kind: 'error' });
      return;
    }
    setState({ kind: 'loading' });
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setState({ kind: 'ok', lat: pos.coords.latitude, lon: pos.coords.longitude });
        // Shared store → the map draws the pilot marker from this.
        useOperatorPos.getState().set({ lat: pos.coords.latitude, lon: pos.coords.longitude });
      },
      () => {
        setState({ kind: 'error' });
        useOperatorPos.getState().set(null);
      },
      { enableHighAccuracy: true, timeout: 10_000 },
    );
  }

  return (
    <span className="hidden items-center gap-1 md:flex">
      <button
        type="button"
        onClick={fetchLocation}
        title={
          state.kind === 'error'
            ? 'Location unavailable — check browser permission, then retry'
            : 'Fetch my location from the browser'
        }
        className={cn(
          'grid h-6 w-6 place-items-center rounded transition-colors hover:bg-surface-2',
          state.kind === 'ok' && 'text-accent',
          state.kind === 'error' && 'text-status-warn',
          (state.kind === 'idle' || state.kind === 'loading') && 'text-fg-muted hover:text-fg-primary',
        )}
      >
        {state.kind === 'loading' ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <MapPin className="h-3.5 w-3.5" />
        )}
      </button>
      {state.kind === 'ok' && (
        <button
          type="button"
          onDoubleClick={() =>
            getActiveMap()?.easeTo({ center: [state.lon, state.lat], zoom: 16, duration: 600 })
          }
          title="My location — double-click to fly there"
          className="font-mono text-[11px] tabular-nums text-fg-secondary hover:text-accent"
        >
          {state.lat.toFixed(5)}, {state.lon.toFixed(5)}
        </button>
      )}
      {state.kind === 'error' && (
        <span className="text-[10px] uppercase tracking-wider text-status-warn">no fix</span>
      )}
    </span>
  );
}

function UserMenu() {
  const username = useAuth((s) => s.username);
  const displayName = useAuth((s) => s.displayName);
  const role = useAuth((s) => s.role);
  const logout = useAuth((s) => s.logout);

  const initials = (displayName ?? username ?? '?')
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <div className="group relative">
      <button
        className="grid h-7 w-7 place-items-center rounded-full border border-border-default bg-surface-2 text-[10px] font-semibold text-fg-secondary hover:bg-surface-3 hover:text-fg-primary"
        title={username ? `${username} · ${role ?? ''}` : 'Account'}
      >
        {initials !== '?' ? initials : <CircleUser className="h-4 w-4" />}
      </button>
      <div className="invisible absolute right-0 top-full z-50 pt-1 opacity-0 transition-opacity group-hover:visible group-hover:opacity-100 focus-within:visible focus-within:opacity-100">
        <div className="w-60 rounded border border-border-default bg-surface-2 p-1 shadow-[var(--shadow-pop)]">
          <div className="flex items-center gap-2 border-b border-border-subtle px-2.5 py-2">
            <div className="grid h-9 w-9 flex-none place-items-center rounded-full border border-border-default bg-surface-3 text-[11px] font-semibold text-fg-secondary">
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-[12px] font-medium text-fg-primary">
                {displayName ?? username ?? '—'}
              </div>
              <div className="truncate font-mono text-[10px] text-fg-muted">
                @{username ?? '—'}
                {role ? ` · ${role}` : ''}
              </div>
            </div>
          </div>
          <button
            onClick={logout}
            className="flex w-full items-center gap-2 rounded px-2.5 py-1.5 text-left text-[12px] text-fg-secondary hover:bg-surface-3 hover:text-status-critical"
          >
            <LogOut className="h-3.5 w-3.5" />
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
